<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ─────────────────────────────────────── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-users" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Employees</h4>
              <small style="opacity: .8;">{{ filteredEmployees.length }} staff member(s)</small>
            </div>
            <div class="ml-auto">
              <button type="button" class="btn btn-outline-light btn-sm font-weight-bold px-3 mr-2" @click="showPerformance = !showPerformance">
                <i class="fe fe-bar-chart-2 mr-1"></i> Enrolment performance
              </button>
              <a class="btn btn-light btn-sm font-weight-bold px-4" href="/add-employee-state">
                <i class="fe fe-plus mr-1"></i> Add Employee
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- ── Enrolment performance (people enrolled per staff member) ── -->
        <OfficerPerformance v-if="showPerformance" closable @close="showPerformance = false" />

        <!-- ── Stats / Export Controls ────────────────────────── -->
        <div class="card action-card shadow mb-4">
          <div class="card-body py-3 px-4">
            <div class="d-flex flex-wrap align-items-center" style="gap: 10px;">
              <button class="btn btn-sm btn-outline-primary" @click="getStats">
                <i class="fe fe-bar-chart-2 mr-1"></i> Get Statistics
              </button>
              <button class="btn btn-sm btn-outline-success">
                <download-excel :data="stats" :fields="json_fields" :escapeCsv="false" name="employees.xls">
                  <i class="fe fe-download mr-1"></i> Export Data
                </download-excel>
              </button>
            </div>
          </div>
        </div>

        <!-- ── Employee Table ──────────────────────────────────── -->
        <div class="card shadow table-card">
          <div class="px-4 pt-4 pb-3">
            <h6 class="mb-3 font-weight-bold text-dark">
              <i class="fe fe-list text-primary mr-2"></i> All Staff
            </h6>
            <form @submit.prevent>
              <div class="form-row">
                <div class="form-group col-md-6 mb-2">
                  <label for="employee-search-filter" class="sr-only">Search staff</label>
                  <input
                    id="employee-search-filter"
                    v-model.trim="searchTerm"
                    type="text"
                    class="form-control"
                    placeholder="Search by name, phone number, or email…"
                  />
                </div>
                <div class="form-group col-6 col-md-2 mb-2">
                  <label for="employee-status-filter" class="sr-only">Status</label>
                  <select id="employee-status-filter" v-model="statusFilter" class="form-control">
                    <option value="">All statuses</option>
                    <option value="active">Active ({{ counts.active }})</option>
                    <option value="inactive">Inactive ({{ counts.inactive }})</option>
                    <option value="halted">Enrolment halted ({{ counts.halted }})</option>
                  </select>
                </div>
                <div class="form-group col-6 col-md-2 mb-2">
                  <label for="employee-access-filter" class="sr-only">Access level</label>
                  <select id="employee-access-filter" v-model="accessFilter" class="form-control">
                    <option value="">Admins &amp; maintainers</option>
                    <option value="admin">Admins ({{ counts.admin }})</option>
                    <option value="maintainer">Maintainers ({{ counts.maintainer }})</option>
                  </select>
                </div>
                <div class="form-group col-md-2 mb-2">
                  <label for="employee-role-filter" class="sr-only">Job role</label>
                  <select id="employee-role-filter" v-model="roleFilter" class="form-control">
                    <option value="">All job roles</option>
                    <option v-for="role in jobRoles" :key="role.value" :value="role.value">{{ role.label }} ({{ role.count }})</option>
                  </select>
                </div>
              </div>
              <button
                v-if="hasActiveFilters"
                type="button"
                class="btn btn-sm btn-outline-secondary mb-2"
                @click="clearFilters"
              >
                <i class="fe fe-x mr-1"></i> Clear Filters
              </button>
            </form>

            <!-- Halt / restore enrolment for the ticked staff (agency admins) -->
            <div v-if="isAgencyAdmin && selectedIds.length" class="bulk-bar d-flex flex-wrap align-items-center mt-2" style="gap: 8px;">
              <span class="small font-weight-bold">{{ selectedIds.length }} selected</span>
              <button type="button" class="btn btn-sm btn-warning" :disabled="bulkBusy" @click="setEnrollmentHalt(true)">
                <i class="fe fe-pause-circle mr-1"></i> Halt enrolment
              </button>
              <button type="button" class="btn btn-sm btn-success" :disabled="bulkBusy" @click="setEnrollmentHalt(false)">
                <i class="fe fe-play-circle mr-1"></i> Allow enrolment
              </button>
              <button type="button" class="btn btn-sm btn-link text-muted" :disabled="bulkBusy" @click="selectedIds = []">Clear selection</button>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table mb-0">
              <thead class="table-head">
                <tr>
                  <th v-if="isAgencyAdmin" class="table-th pl-4 pr-0" style="width: 1%;">
                    <input type="checkbox" aria-label="Select all staff shown" :checked="allShownSelected"
                      :indeterminate.prop="someShownSelected && !allShownSelected" @change="toggleAllShown($event.target.checked)" />
                  </th>
                  <th class="table-th px-4">Name</th>
                  <th class="table-th">Email</th>
                  <th class="table-th">Phone Number</th>
                  <th class="table-th">Job Role</th>
                  <th class="table-th">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="employee in filteredEmployees" :key="employee.id" class="table-row">
                  <td v-if="isAgencyAdmin" class="pl-4 pr-0 py-3">
                    <input type="checkbox" :value="employee.id" v-model="selectedIds"
                      :aria-label="'Select ' + employee.firstname + ' ' + employee.lastname" />
                  </td>
                  <td class="px-4 py-3">
                    <div class="d-flex align-items-center" style="gap: 10px;">
                      <div class="employee-avatar">
                        {{ (employee.firstname || '?')[0].toUpperCase() }}
                      </div>
                      <div>
                        <p class="mb-0 font-weight-bold" style="font-size: .88rem;">
                          {{ employee.firstname }} {{ employee.lastname }}
                        </p>
                        <span class="staff-chip" :class="isAdmin(employee) ? 'staff-chip--admin' : 'staff-chip--maintainer'">
                          <i :class="isAdmin(employee) ? 'fe fe-shield' : 'fe fe-tool'"></i>
                          {{ isAdmin(employee) ? 'Admin' : 'Maintainer' }}
                        </span>
                        <span class="staff-chip" :class="employee.blocked_at ? 'staff-chip--inactive' : 'staff-chip--active'"
                          :title="employee.blocked_at ? 'Blocked on ' + employee.blocked_at : ''">
                          {{ employee.blocked_at ? 'Inactive' : 'Active' }}
                        </span>
                        <span v-if="employee.enrollment_halted_at" class="staff-chip staff-chip--halted"
                          :title="'Enrolment halted on ' + employee.enrollment_halted_at">
                          <i class="fe fe-pause-circle"></i> Enrolment halted
                        </span>
                      </div>
                    </div>
                  </td>
                  <td class="py-3" style="font-size: .88rem;">{{ employee.email }}</td>
                  <td class="py-3" style="font-size: .88rem;">{{ employee.phone_number }}</td>
                  <td class="py-3" style="font-size: .88rem;">{{ employee.job_title || '—' }}</td>
                  <td class="py-3">
                    <router-link :to="{ path: '/employee/' + employee.id }">
                      <button type="button" class="btn btn-sm btn-outline-primary">
                        <i class="fe fe-eye mr-1"></i> View
                      </button>
                    </router-link>
                    <button v-if="canDelete(employee)" type="button" class="btn btn-sm btn-outline-danger ml-1"
                      :aria-label="'Delete ' + employee.firstname + ' ' + employee.lastname" @click="deleteUser(employee)">
                      <i class="fe fe-trash-2"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="filteredEmployees.length === 0">
                  <td :colspan="isAgencyAdmin ? 6 : 5" class="text-center text-muted py-5">
                    <i class="fe fe-users" style="font-size: 2rem; display: block; margin-bottom: 8px;"></i>
                    No employees found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  import OfficerPerformance from "@/views/analytics/components/OfficerPerformance.vue";
  // Import component
  import Loading from "vue-loading-overlay";
  // Import stylesheet
  import "vue-loading-overlay/dist/vue-loading.css";
  // Init plugin

  export default {
    components: { OfficerPerformance,
      Navbar,
      Loading,
    },
    data() {
      return {
      showPerformance: false,
        isLoading: false,
        fullPage: true,
        states: "",
        employees: "",
        searchTerm: "",
        statusFilter: "",
        accessFilter: "",
        roleFilter: "",
        selectedIds: [],
        bulkBusy: false,
        user: null,
        state: "",
        lga_states: "",
        stats: "",
        json_fields: {
          "Full Name": "full_name",
          "E - mail": "email",
          "Phone Number": "phone_number",
          "Role": "job_title",
          "Total ": "staffenrollees_count",
          // "Dependent Count": "staffdependants_count",
          Total: "total",
        },
        json_data: [],
        json_meta: [
          [
            {
              key: "charset",
              value: "utf-8",
            },
          ],
        ],
      };
    },
    computed: {
      employeeRows() {
        return Array.isArray(this.employees && this.employees.data)
          ? this.employees.data
          : [];
      },
      counts() {
        const rows = this.employeeRows;
        const admins = rows.filter((e) => this.isAdmin(e)).length;
        const inactive = rows.filter((e) => e.blocked_at).length;
        const halted = rows.filter((e) => e.enrollment_halted_at).length;
        return { admin: admins, maintainer: rows.length - admins, inactive, active: rows.length - inactive, halted };
      },
      // Job roles present in the list, with counts ("No job role" for staff without one).
      jobRoles() {
        const counts = {};
        this.employeeRows.forEach((e) => {
          const key = (e.job_title || "").trim();
          counts[key] = (counts[key] || 0) + 1;
        });
        return Object.keys(counts)
          .sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)))
          .map((key) => ({ value: key === "" ? "__none__" : key, label: key || "No job role", count: counts[key] }));
      },
      filteredEmployees() {
        const searchTerm = this.searchTerm.toLowerCase();

        return this.employeeRows.filter((employee) => {
          if (this.statusFilter === "active" && employee.blocked_at) return false;
          if (this.statusFilter === "inactive" && !employee.blocked_at) return false;
          if (this.statusFilter === "halted" && !employee.enrollment_halted_at) return false;
          if (this.accessFilter === "admin" && !this.isAdmin(employee)) return false;
          if (this.accessFilter === "maintainer" && this.isAdmin(employee)) return false;
          if (this.roleFilter) {
            const role = (employee.job_title || "").trim();
            if (this.roleFilter === "__none__" ? role !== "" : role !== this.roleFilter) return false;
          }
          if (!searchTerm) return true;

          const fullName = `${employee.firstname || ""} ${employee.lastname || ""}`
            .trim()
            .toLowerCase();
          const employeePhone = (employee.phone_number || "").toLowerCase();
          const employeeEmail = (employee.email || "").toLowerCase();

          return (
            fullName.includes(searchTerm) ||
            employeePhone.includes(searchTerm) ||
            employeeEmail.includes(searchTerm)
          );
        });
      },
      allShownSelected() {
        return this.filteredEmployees.length > 0 && this.filteredEmployees.every((e) => this.selectedIds.includes(e.id));
      },
      someShownSelected() {
        return this.filteredEmployees.some((e) => this.selectedIds.includes(e.id));
      },
      hasActiveFilters() {
        return Boolean(this.searchTerm || this.statusFilter || this.accessFilter || this.roleFilter);
      },
      // Agency admins (the agency account, or staff with user_role 1) can delete staff.
      isAgencyAdmin() {
        return !!this.user && (this.user.type === "shis" || (this.user.type === "employee" && Number(this.user.user_role) === 1));
      },
    },
    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user"));
    },
    methods: {
      clearFilters() {
        this.searchTerm = "";
        this.statusFilter = "";
        this.accessFilter = "";
        this.roleFilter = "";
      },
      isAdmin(employee) {
        return Number(employee.user_role) === 1;
      },
      canDelete(employee) {
        return this.isAgencyAdmin && !!this.user && Number(employee.id) !== Number(this.user.id);
      },
      getEmployees() {
        this.axios
          .get(`/api/v1/auth/getEmployee/90`)
          .then((response) => {
            this.employees = response.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },

      getStats() {
        this.isLoading = true;
        this.axios
          .get(`/api/v1/auth/getEmployee/stats/90`)
          .then((response) => {
            this.isLoading = false;

            this.stats = response.data;
            console.log(response);
            this.$toasted.info("Fetched Successfully", {
              position: "top-center",
              duration: 5000,
            });
          })
          .catch((error) => {
            console.error(error);
            this.isLoading = false;

          });
      },
      // Ticks or unticks every staff member the filters currently show.
      toggleAllShown(checked) {
        const shown = this.filteredEmployees.map((e) => e.id);
        this.selectedIds = checked
          ? Array.from(new Set(this.selectedIds.concat(shown)))
          : this.selectedIds.filter((id) => !shown.includes(id));
      },
      // Halted staff can't enrol enrollees or dependants until an admin allows it again.
      setEnrollmentHalt(halted) {
        const count = this.selectedIds.length;
        const question = halted
          ? `Halt enrolment for ${count} staff member(s)? They won't be able to enrol enrollees or dependants.`
          : `Allow ${count} staff member(s) to enrol again?`;
        if (!count || !confirm(question)) return;
        this.bulkBusy = true;
        this.axios
          .post("/api/v1/auth/agency-staff/enrollment-halt", { ids: this.selectedIds, halted })
          .then((response) => {
            this.$toasted.success(response.data.message, { position: "top-center", duration: 3000 });
            this.selectedIds = [];
            this.getEmployees();
          })
          .catch((error) => {
            const message = (error.response && error.response.data && error.response.data.message) || "Could not update enrolment access";
            this.$toasted.error(message, { position: "top-center", duration: 4000 });
          })
          .finally(() => {
            this.bulkBusy = false;
          });
      },
      deleteUser(employee) {
        const name = `${employee.firstname || ""} ${employee.lastname || ""}`.trim() || "this staff member";
        if (!confirm(`Delete ${name}? They will no longer be able to sign in.`)) return;
        this.axios
          .delete(`/api/v1/auth/agency-staff/${employee.id}`)
          .then(() => {
            this.$toasted.success(`${name} deleted`, { position: "top-center", duration: 3000 });
            this.getEmployees();
          })
          .catch((error) => {
            const message = (error.response && error.response.data && error.response.data.message) || "Could not delete the staff member";
            this.$toasted.error(message, { position: "top-center", duration: 4000 });
          });
      },
    },
    created() {
      this.getEmployees();
    },
  };
</script>

<style scoped>
  /* ── Page Header ─────────────────────────────────────── */
  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    padding-bottom: 60px;
  }

  .container.mt-n3 {
    margin-top: -32px !important;
  }

  .header-icon-wrap {
    width: 52px;
    height: 52px;
    background: rgba(255, 255, 255, 0.18);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* ── Action Bar ────────────────────────────────────────── */
  .action-card {
    border-radius: 14px !important;
    border: none !important;
  }

  /* ── Employee Avatar ─────────────────────────────────── */
  .employee-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    color: #fff;
    font-size: 0.85rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* ── Status badge ─────────────────────────────────────── */
  .status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .status-badge--vetted {
    background: #eff6ff;
    color: #1d4ed8;
  }

  /* ── Table ─────────────────────────────────────────────── */
  .table-card {
    border-radius: 14px !important;
    border: none !important;
    overflow: hidden;
  }

  .table-head {
    background: #f8fafc;
  }

  .table-th {
    font-size: 0.75rem !important;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b !important;
    font-weight: 600 !important;
    border-bottom: 2px solid #e2e8f0 !important;
    padding: 12px 16px !important;
    white-space: nowrap;
  }

  .table-row {
    border-color: #f1f5f9 !important;
    transition: background 0.15s;
  }

  .table-row:hover {
    background: #f8fafc !important;
  }
  .staff-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 1px 8px;
    border-radius: 20px;
    font-size: .7rem;
    font-weight: 600;
    margin-right: 4px;
  }
  .staff-chip--admin { background: #ede9fe; color: #5b21b6; }
  .staff-chip--maintainer { background: #e0f2fe; color: #075985; }
  .staff-chip--active { background: #d1fae5; color: #065f46; }
  .staff-chip--inactive { background: #fee2e2; color: #991b1b; }
  .staff-chip--halted { background: #fef3c7; color: #92400e; }
  .bulk-bar { background: #f1f5f9; border-radius: 8px; padding: 8px 12px; }
</style>
